import * as Popover from '@radix-ui/react-popover';
import {Tooltip} from '@wandb/weave/components/Tooltip';
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {useIsAdminModeActiveAndIsNotLocalEnv, viewingAs} from '../util/admin';
import {usePublicQueueAccess} from './Launch/hooks';
import {usePublicQueues, useQueue} from './Launch/Queues/hooks';
import {RunQueueItemsPopoverContent} from './Launch/RunQueueItemsPopoverContent';
import {WANDB_ENTITY_NAME, WANDB_PUBLIC_QUEUE_NAME} from './Launch/utils';
import * as S from './RunQueueItemsPopover.styles';

interface RunQueuePopoverContextType {
  openPopover: () => void;
  registerPopover: (openFn: () => void) => void;
  unregisterPopover: () => void;
}

const RunQueuePopoverContext = createContext<RunQueuePopoverContextType | null>(
  null
);

export const useRunQueuePopover = () => {
  const context = useContext(RunQueuePopoverContext);
  if (!context) {
    throw new Error(
      'useRunQueuePopover must be used within RunQueuePopoverProvider'
    );
  }
  return context;
};

export const RunQueuePopoverProvider: React.FC<{children: React.ReactNode}> = ({
  children,
}) => {
  const popoverOpenFnRef = useRef<(() => void) | null>(null);

  const openPopover = () => {
    if (popoverOpenFnRef.current) {
      popoverOpenFnRef.current();
    }
  };

  const registerPopover = (openFn: () => void) => {
    popoverOpenFnRef.current = openFn;
  };

  const unregisterPopover = () => {
    popoverOpenFnRef.current = null;
  };

  return (
    <RunQueuePopoverContext.Provider
      value={{openPopover, registerPopover, unregisterPopover}}>
      {children}
    </RunQueuePopoverContext.Provider>
  );
};

export const RunQueueItemPopoverInner: React.FC = () => {
  const {
    queues,
    loading: queuesLoading,
    error: publicQueuesError,
  } = usePublicQueues(false);

  const publicQueue = queues.find(
    q =>
      q.name === WANDB_PUBLIC_QUEUE_NAME && q.entityName === WANDB_ENTITY_NAME
  );

  const {
    queue,
    loading: queueLoading,
    refetch,
    error,
  } = useQueue(WANDB_ENTITY_NAME, publicQueue?.id ?? '');

  if (queuesLoading || queueLoading || !queue || error || publicQueuesError) {
    return null;
  }

  return <RunQueueItemsPopoverContent queue={queue} refetchQueue={refetch} />;
};

export const RunQueueItemPopover: React.FC = () => {
  const isAdminMode =
    useIsAdminModeActiveAndIsNotLocalEnv() || viewingAs() !== '';
  const {hasPublicQueueAccess, isFeatureFlagLoading} = usePublicQueueAccess();
  const [open, setOpen] = useState(false);

  const context = useContext(RunQueuePopoverContext);

  // Register this popover with the global context
  useEffect(() => {
    if (context) {
      const openFn = () => {
        setOpen(true);
      };
      context.registerPopover(openFn);
      return () => {
        context.unregisterPopover();
      };
    }
    return undefined;
  }, [context]);

  if (isFeatureFlagLoading || !hasPublicQueueAccess) {
    return <></>;
  }

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <S.IconContainer>
        <Tooltip
          position="bottom center"
          trigger={
            <Popover.Trigger asChild>
              <S.IconWrapper
                $showAdminBanner={isAdminMode}
                $isOpen={open}
                // prevent surrounding text selection when clicking the icon
                onMouseDown={e => e.preventDefault()}>
                <S.Icon aria-label="run queue items" />
              </S.IconWrapper>
            </Popover.Trigger>
          }>
          View In Progress Runs
        </Tooltip>
      </S.IconContainer>

      <Popover.Portal>
        <Popover.Content
          side="bottom"
          align="start"
          sideOffset={4}
          alignOffset={16}
          style={{outline: 'none', zIndex: 9999}}>
          <RunQueueItemPopoverInner />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
